import { useUser } from "@customer-tools-web/component-authentication";
import { useI18N } from "@customer-tools-web/component-i18n";
import {
  isMobile,
  isTablet,
  media,
  theme,
  Avatar,
  Icon,
} from "@customer-tools-web/component-ui";
import {
  useMarketConfig,
  MarketConfig,
} from "@customer-tools-web/market-extension-resolver";
import { Popover } from "antd";
import { cloneDeep } from "lodash";
import * as React from "react";
import styled, { createGlobalStyle, css } from "styled-components";

import { MenuItemType, TrackMenuType } from "../Header";
import { useDrawer } from "../components/Drawer";
import { useMenus, Menu_Keys_Type } from "../components/Menus";
import { NavigatorLink } from "../components/NavigatorLink";
import { generateSharpieImgUrl, matchActiveKey } from "../components/tool";

import MessageMenu from "./MessageMenu";
import NotificationMenu from "./NotificationMenu";

interface Props {
  ctServer: string;
  menus: MenuItemType[];
  screenWidth?: number;
  trackMenuTap?: TrackMenuType;
  braze?: any;
}

export function AccountMenu(props: Props) {
  const { showDrawer, renderDrawer, closeDrawer } = useDrawer();
  const { renderMenus, renderButtons } = useMenus();
  const { trackMenuTap, screenWidth, braze, ctServer } = props;
  const [popoverVisible, setPopoverVisible] = React.useState(false);

  const config = useMarketConfig();
  const { avatar, email, fullName, permissions } = useUser();
  const { parseToLocaleText } = useI18N();

  function hidePopover() {
    setPopoverVisible(false);
    closeDrawer();
  }

  function onClick(key: Menu_Keys_Type, close: boolean = true) {
    const trackMenuTapWithCurrentUrl = (
      (currentUrl: string) => (menuName: MenuItemType["key"]) =>
        trackMenuTap && trackMenuTap(menuName, currentUrl)
    )(window.location.pathname);
    trackMenuTapWithCurrentUrl(key);
    if (close) {
      hidePopover();
    }
  }
  function renderDesktop() {
    const menus = generateMenuList(true);
    const currentKey = matchActiveKey(menus);
    const sharpieAvatarSrc = generateSharpieImgUrl(config, avatar || "");
    const isShowDrawer = isTablet(screenWidth!);
    const isSmallSceen = isMobile(screenWidth!);
    return (
      <AccountWrapper data-user-email={email}>
        {process.env.REACT_APP_FEATURE_BRAZE === "on" && (
          <NotificationMenu
            data-test={`btn-notification-menu`}
            braze={braze}
            ctServer={ctServer}
          />
        )}
        {process.env.REACT_APP_FEATURE_BRAZE === "on" &&
          permissions.lead.canViewLead && (
            <MessageMenu braze={braze} ctServer={ctServer} />
          )}
        {isShowDrawer &&
          renderDrawer({
            children: renderMenus({
              mode: "inline",
              currentKey,
              onClick,
              menus: props.menus,
            }),
            title: (
              <TitleWrapper>
                <UserInfoWrapper>
                  <UserName title={parseToLocaleText(fullName)}>
                    {parseToLocaleText(fullName)}
                  </UserName>
                  <UserEmail title={email!}>{email}</UserEmail>
                </UserInfoWrapper>
              </TitleWrapper>
            ),
            footer: (
              <ButtonGroup>
                {renderButtons({
                  onClick,
                  menus: props.menus,
                })}
              </ButtonGroup>
            ),
            placement: "right",
          })}
        <Popover
          title={
            <UserInfoWrapper>
              <UserName title={parseToLocaleText(fullName)}>
                {parseToLocaleText(fullName)}
              </UserName>
              <UserEmail title={email!}>{email}</UserEmail>
            </UserInfoWrapper>
          }
          content={getPopContent()}
          overlayClassName="accountInfo-overlay"
          trigger="click"
          visible={!isShowDrawer && popoverVisible}
          onVisibleChange={!isShowDrawer ? setPopoverVisible : undefined}
          placement="bottomRight"
        >
          <ImageWrapper
            className="image-wrapper"
            onClick={isShowDrawer ? showDrawer : undefined}
          >
            <UserAvatar
              className="user-avatar"
              src={sharpieAvatarSrc}
              alt={email!}
            />
            {!isSmallSceen && (
              <Icon
                type={popoverVisible ? "arrow-up" : "arrow-down"}
                color={theme.textDeepGray}
              />
            )}
          </ImageWrapper>
        </Popover>
      </AccountWrapper>
    );
  }

  function getPopContent() {
    const menuList: MenuItemType[] = generateMenuList();
    const activeKey = matchActiveKey(menuList, true);
    const { trackMenuTap } = props;
    return (
      <>
        <AccountMenuContainer>
          {menuList.map((item) =>
            item.getCustomNode ? null : (
              <AccountMenuItem
                key={item.key}
                className={activeKey === item.key ? "select-item" : undefined}
                onClick={() => {
                  if (trackMenuTap) {
                    trackMenuTap(item.key);
                  }
                  hidePopover();
                }}
                config={config}
              >
                {item.icon && (
                  <MenuIcon
                    iconSize={item?.iconSize}
                    iconMarginRight={item?.iconMarginRight}
                    type={item.icon}
                  />
                )}
                <NavigatorLink
                  itemRouterOptions={item.routerOptions}
                  itemLink={item.link || "#"}
                  itemLabel={item.label}
                  dataTest={item.dataTest}
                  shouldUseRouter={item.shouldUseRouter}
                  openInSameTab={item.openInSameTab}
                  onClick={
                    item.click &&
                    (() => {
                      item.click!();
                    })
                  }
                />
                {item.needOpenIcon && <MenuIcon type="external-link-lg" />}
              </AccountMenuItem>
            )
          )}
        </AccountMenuContainer>
        {renderButtons({
          menus: menuList,
          onClick,
        })}
      </>
    );
  }
  function generateMenuList(isMobile: boolean = false) {
    const { menus } = props;
    if (isMobile) {
      const mobileMenus: MenuItemType[] = cloneDeep(menus);
      return mobileMenus.map((item) => {
        if (item.dataTest) {
          item.dataTest = item.dataTest + "-mobile";
        }
        return item;
      });
    }
    return menus;
  }
  return (
    <>
      <GlobalStyle />
      {renderDesktop()}
    </>
  );
}
export default AccountMenu;
export { ButtonGroup };

const AccountWrapper = styled.div`
  display: flex;
  position: absolute;
  bottom: 0;
  right: 18px;
  height: 50px;
  line-height: 50px;
  align-items: center;
  z-index: 1;
  background: rgba(0, 0, 0, 0);
`;

const GlobalStyle = createGlobalStyle`
 .ant-popover.accountInfo-overlay {
   display: none;
   left: 0px !important;
   float: right;
   position:relative;
   ${media.md`
     display: block;
   `};
   &.ant-popover-hidden {
     display: none;
   }
   width: 270px;
   .ant-popover-title {
    padding: 16px;
    font-size: ${(props) => props?.theme?.fontSize?.caption};
    font-weight: 400;
    color: ${theme.textDeepGray};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
   }
   .ant-popover-content {
    margin-top: -10px;
    position: relative;
    top: -4px;
   }
   .ant-popover-inner-content {
    padding: 16px;
   }
   > .ant-popover-content > .ant-popover-arrow {
     display: none;
   }
 }
`;

const TitleWrapper = styled.div`
  display: flex;
  overflow: hidden;
  padding-top: 38px;
`;

const UserInfoWrapper = styled.div`
  :not(:first-child) {
    margin-left: 12px;
  }
  overflow: hidden;
`;

const UserName = styled.label`
  display: block;
  font-size: ${(props) => props?.theme?.fontSize?.body};
  font-weight: 400;
  line-height: 2rem;
  color: ${(props) => props.theme.black};
  text-overflow: ellipsis;
  overflow: hidden;
`;

const UserEmail = styled.label`
  display: block;
  font-size: ${(props) => props?.theme?.fontSize?.caption};
  font-weight: 400;
  line-height: 2rem;
  color: ${(props) => props.theme.textDeepGray};
  text-overflow: ellipsis;
  overflow: hidden;
`;

const ImageWrapper = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  > * {
    cursor: pointer;
  }
`;

const UserAvatar = styled(Avatar)`
  margin-right: 4px;
  flex: none;
`;

const AccountMenuContainer = styled.ul`
  margin: 0;
  padding-left: unset;
`;

const ButtonGroup = styled.div`
  padding: 0 16px;
  margin-bottom: 25px;
`;

const agentNetIconStyles = `
  font-size: 18px;
  margin-left 3px;
  margin-right: 10px;
`;

const externalLinkIconStyles = `
  font-size: 18px;
  margin-left: 2px;
`;

const MenuIcon = styled(Icon)<{ iconSize?: string; iconMarginRight?: string }>`
  margin-right: ${(props) =>
    props.iconMarginRight ? props.iconMarginRight : "8px"};
  color: ${theme.textDeepGray};
  font-size: ${(props) =>
    props?.iconSize ? props.iconSize : props?.theme?.fontSize?.title1};
  ${(props) => props.type === "agent-net-icon" && agentNetIconStyles}
  ${(props) => props.type === "external-link-lg" && externalLinkIconStyles}
`;

const AccountMenuItem = styled.li<{ config: MarketConfig }>`
  display: flex;
  padding: 16px 0;
  font-size: ${(props) => props?.theme?.fontSize?.body};
  font-weight: 400;
  line-height: 20px;
  color: ${theme.textColor};
  border-bottom: 1px solid ${theme.bgGray};
  user-select: none;
  &:first-child {
    padding-top: 0;
  }
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    color: ${theme.menuHoverColor};
  }

  a {
    margin-right: 8px;
    color: ${theme.textColor};
    &:focus {
      color: ${theme.textColor};
      text-decoration: none;
    }
    &:hover {
      color: ${theme.menuHoverColor};
    }
  }

  &.select-item,
  &.select-item > a,
  &.select-item > ${/* sc-selector */ Icon} {
    color: ${theme.menuActiveColor};
  }

  &:hover > a,
  & > a:hover,
  & > a:focus,
  &:hover > ${/* sc-selector */ Icon}, & > ${/* sc-selector */ Icon}:hover {
    ${({ config }) =>
      config &&
      css`
        color: ${theme.menuHoverColor};
      `}
  }
  &.disabled {
    ${/* sc-selector */ Icon} {
      color: ${theme.textGray};
    }
    color: ${theme.textGray};
    cursor: not-allowed;
  }
`;
